import { initFirebase } from '../firebase'
import { collection, getDocs, getCountFromServer, query, orderBy, startAfter, limit, documentId } from 'firebase/firestore'

const { db } = initFirebase()

const BATCH = 500

// iOS joins multi-selected reasons into a single comma-separated string.
// Legacy/test rows store the direction (up/down) instead of a real reason; those are not reasons.
const JUNK_REASONS = new Set(['up', 'down', 'like', 'dislike', ''])

function parseReasons(feedback) {
  if (!feedback || typeof feedback !== 'string') return []
  return feedback
    .split(',')
    .map(s => s.trim())
    .filter(s => s && !JUNK_REASONS.has(s.toLowerCase()))
}

// Recursively finds every object carrying a `likeDislike` block (= a card).
// The object's `title` labels the feedback.
function collectCards(node, onCard) {
  if (!node || typeof node !== 'object') return
  if (Array.isArray(node)) {
    node.forEach(n => collectCards(n, onCard))
    return
  }
  if (node.likeDislike && typeof node.likeDislike === 'object' && 'like' in node.likeDislike) {
    onCard(node)
  }
  for (const value of Object.values(node)) {
    if (value && typeof value === 'object') collectCards(value, onCard)
  }
}

function toMs(v) {
  if (!v) return 0
  if (typeof v === 'object' && v.seconds) return v.seconds * 1000
  const t = new Date(v).getTime()
  return isNaN(t) ? 0 : t
}

export class FirebaseContentFeedbackService {
  // Feedback is not a standalone collection; it is embedded inside generated content
  // (birthChartV2 / compatibility cards). It cannot be queried, so the collections are
  // scanned in pages, reporting progress via onProgress({ phase, processed, total, found }).
  async getAllFeedback(onProgress) {
    const usersCol = collection(db, 'users')
    const compatCol = collection(db, 'compatibility')

    const [usersTotal, compatTotal] = await Promise.all([
      getCountFromServer(usersCol).then(s => s.data().count).catch(() => 0),
      getCountFromServer(compatCol).then(s => s.data().count).catch(() => 0)
    ])
    const total = usersTotal + compatTotal

    const result = []
    let processed = 0
    const report = phase => {
      if (onProgress) onProgress({ phase, processed, total, found: result.length })
    }
    report('users')

    await this._scanPaged(usersCol, docSnap => {
      const data = docSnap.data()
      const bc = data.birthChartV2
      if (!bc) return
      const userId = data.uid || docSnap.id
      collectCards(bc, card => result.push(this._toEntry({ surface: 'Birth Chart', card, userId, rawDate: bc.generatedAt })))
    }, size => { processed += size; report('users') })

    report('compatibility')
    await this._scanPaged(compatCol, docSnap => {
      const data = docSnap.data()
      if (data.isDeleted) return
      const userId = data.userId || ''
      const rawDate = data.updatedAt || data.generatedAt || data.createdAt
      collectCards(data, card => result.push(this._toEntry({ surface: 'Compatibility', card, userId, rawDate })))
    }, size => { processed += size; report('compatibility') })

    report('done')

    result.sort((a, b) => (b.timestampMs || 0) - (a.timestampMs || 0))
    result.forEach((e, i) => { e.id = `${e.surface}-${i}` })
    return result
  }

  async _scanPaged(col, onDoc, onPage) {
    let last = null
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const q = last
        ? query(col, orderBy(documentId()), startAfter(last), limit(BATCH))
        : query(col, orderBy(documentId()), limit(BATCH))
      const snap = await getDocs(q)
      if (snap.empty) break
      snap.forEach(onDoc)
      last = snap.docs[snap.docs.length - 1]
      if (onPage) onPage(snap.size)
      if (snap.size < BATCH) break
    }
  }

  _toEntry({ surface, card, userId, rawDate }) {
    const ld = card.likeDislike || {}
    return {
      surface,
      like: ld.like === true,
      reasons: parseReasons(ld.feedback),
      rawFeedback: ld.feedback || '',
      cardTitle: card.title || card.name || card.label || '—',
      userId,
      timestampMs: toMs(rawDate),
      rawDate
    }
  }
}
